import React, { useState } from 'react'
import { User } from 'lucide-react'
import { Badge, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, CopyableId, CopyableText } from '@duck/ui'
import { UserAvatar } from '@/components/UserAvatar'
import { useTar } from '@/modules'
import { useUserDetails } from '@/hooks/useUserDetails'
import { formatRole, getRoleBadgeColor } from '@/components/AdminUsers/userAdmin.utils'
import { UserService } from '@duck/api-definitions'

export interface UserBadgeProps {
  userId: string
  firstName?: string
  lastName?: string
  fullName?: string
  role?: string
  avatar?: string
  className?: string
}

export const UserBadge: React.FC<UserBadgeProps> = ({
  userId,
  firstName,
  lastName,
  fullName,
  role,
  avatar,
  className,
}) => {
  const { t } = useTar()
  const [open, setOpen] = useState(false)
  const { user, challenges, assignments, isLoading } = useUserDetails(userId, { enabled: open })

  const label =
    firstName || lastName
      ? `${firstName ?? ''} ${lastName ?? ''}`.trim()
      : (fullName ?? userId)

  const openPage = () =>
    window.open(`/admin/users/${encodeURIComponent(userId)}`, '_blank', 'noopener,noreferrer')

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      openPage()
    }
  }

  const tipFirst = user?.firstName ?? firstName
  const tipLast = user?.lastName ?? lastName
  const tipRole = user?.role ?? role

  return (
    <TooltipProvider>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          {/* span role="button" avoids nesting a <div> (Badge) inside a <button> */}
          <span
            role="button"
            tabIndex={0}
            onClick={openPage}
            onKeyDown={handleKeyDown}
            className={className}
          >
            <Badge
              variant="outline"
              className="gap-1.5 cursor-pointer border-primary/30 bg-primary/10 text-foreground hover:bg-primary/20 max-w-full"
            >
              <User className="w-3.5 h-3.5 shrink-0 text-primary" />
              <span className="truncate">{label}</span>
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent className="w-72 p-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <UserAvatar
                size="md"
                user={{
                  avatar: user?.avatar ?? avatar,
                  firstName: tipFirst,
                  lastName: tipLast,
                }}
              />
              <div className="min-w-0">
                <p className="font-semibold text-foreground truncate">
                  {`${tipFirst ?? ''} ${tipLast ?? ''}`.trim() || label}
                </p>
                {tipRole && (
                  <Badge className={getRoleBadgeColor(tipRole as UserService.UserRole)}>
                    {formatRole(tipRole)}
                  </Badge>
                )}
              </div>
            </div>
            {isLoading ? (
              <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
            ) : user ? (
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-1" dir="ltr">
                  <span className="text-muted-foreground">{t('common.id')}:</span>
                  <CopyableId value={user.id} />
                </div>
                <div className="flex items-center gap-1" dir="ltr">
                  <span className="text-muted-foreground">{t('common.email')}:</span>
                  <CopyableText value={user.email}>
                    <span className="font-mono break-all">{user.email}</span>
                  </CopyableText>
                </div>
                <p className="text-muted-foreground">
                  {t('users.badgeActivitySummary', {
                    challenges: challenges?.length ?? 0,
                    assignments: assignments?.length ?? 0,
                  })}
                </p>
              </div>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
